import { useEffect, useState } from 'react';
import { fetchTopQuestionsAsked } from '../services/dashboard.service';
import { useFilterContext } from '../usageandbilling/FilterContext';

export interface TopQuestion {
  question: string;
  /** Trimmed/lowercased grouping key — pass this (not `question`) when drilling into a question's detail. */
  normalizedMessage: string;
  count: number;
}

export function useTopQuestionsAsked() {
  const { selectedOrgIds, selectedUserIds, dateRange } = useFilterContext();
  const [data, setData] = useState<TopQuestion[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchQuestions = async () => {
      if (!dateRange[0] || !dateRange[1]) return;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
        });
        if (selectedOrgIds.length > 0) params.set('orgIds', selectedOrgIds.join(','));
        if (selectedUserIds.length > 0) params.set('userIds', selectedUserIds.join(','));

        const response = await fetchTopQuestionsAsked(params.toString(), controller.signal);
        setData((response as { topQuestions: TopQuestion[] }).topQuestions);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setError('Failed to load top questions asked.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
    return () => controller.abort();
  }, [selectedOrgIds, selectedUserIds, dateRange]);

  return { data, loading, error };
}
